import { textureSmoke } from './smoke';
import { bossDeathAge } from './defeat';
import { authoredGeometry } from './authoredGeometry';
/**
 * Plan 19 D3 — the L3 boss as an authored low-flying HELICOPTER, the `tank.ts` pattern (design D3).
 * Three merged meshes on one root: `boss-torso` (fuselage + tail boom + fin + skids + rocket pods +
 * engine housing — the probe watch name AND the High-tier shadow-caster contract survive the swap),
 * `boss-rotor` (hub + 4 thin blades, spins) and `boss-tail-rotor` (smaller, spins faster). No
 * skeleton and no turret node: the FUSELAGE itself yaws to aim, and the rotor discs hang off
 * `boss-rotor-node` / `boss-tail-rotor-node` so their shaft spin composes with the hull attitude.
 *
 * The `bossPose` discipline VERBATIM (plan 05 Design 1): every term is a CLOSED FORM of the
 * snapshot — no dt, no previous-frame memory, no RNG, no clock — so a paused frame freezes exactly
 * and a replay matches. Shared with the tank wherever the maths is genuinely the same (plan-gate r1
 * copilot P2): the ballistic arc (`arcPoint`), the strafe derivative (`bossStrafeVel`), the aim
 * freeze/ease (`tankAimYaw`, heli's own standoff), the flight gate (`shellAlive`) and the band/zone
 * reticle constants (`TANK_PARTS`). What is heli-only: the hover, the bank sign (it banks INTO the
 * slide, the opposite convention to the tank), the rotor spin/spin-down and the death settle.
 *
 * Combat time freezes at the win. A separate bounded presentation age plays rotor coast,
 * settle, droop and smoke before the results overlay; strafe and hover stay at the win snapshot.
 * The inspector supplies the same presentation-age argument.
 *
 * Pinned to the sim contract (plan 19 D1):
 *  - the rest-pose rendered pod world z equals EXACTLY `boss.z + HELI_ROCKET_DZ` — the same constant
 *    the sim stamps on `bossFire.z`, so the tracer starts where the event says the rocket left;
 *  - unlike the tank there is NO barrel-walk: `heliMuzzle.x` is the strafe x (the salvo fires from
 *    the hull centre-line between the two pods), exactly as `bossFire.x` is the strafe x;
 *  - `bossFireAt < 0` (t = 0 / restart) ⇒ rest pose. For a rotor, "rest" means SPINNING: the disc is
 *    at speed for the whole fight, so the guard pins the authored blade angle at t = 0 (rotorSpin(0)
 *    === 0), not a stopped disc;
 *  - the rocket is a RENDER-ONLY deterministic arc from the rendered pod at fire to the locked band
 *    centre at `tankBandZ(spec)` (the aim-cycle band z, used by both fire-cycle bosses), with the
 *    tank's ground-reticle telegraph at that same z.
 */
import {
  Color3,
  Constants,
  Matrix,
  Mesh,
  MeshBuilder,
  Quaternion,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core/pure';
import type { PBRMaterial, Scene } from '@babylonjs/core/pure';
import type { RenderMaterials } from './scene';
import { BRIDGE_HALF_WIDTH } from './world';
import { applyBossGlow, toWorldX } from './actors';
import { BOSS_DEATH_HOLD } from './boss.skeletal';
import { bossStrafeX, tankBandZ, type SimState } from '../sim/sim';
import { HELI_ROCKET_DZ, TANK_ZONE_HALF_X, type BossSpec, type LevelSpec } from '../sim/level';
import {
  arcPoint,
  bossStrafeVel,
  shellAlive,
  shellEnd,
  TANK_MOTION,
  TANK_PARTS,
  tankAimYaw,
  type TankPoint,
} from './tank';

/** The heli stays on the bridge for exactly the brute/tank hold — one number, three bosses. */
export const HELI_DEATH_HOLD = BOSS_DEATH_HOLD;

/** Total triangle budget for the three merged parts (TANK_TRI_BUDGET's number, test-pinned). */
export const HELI_TRI_BUDGET = 9000;

/**
 * Hull CENTRE height above the deck at hover (m). D3: low flight — the skids hang ~1.2 m below it
 * (still clear of the deck) and the rotor disc tops out ≈ 1.2 m above, i.e. under the 5.2 m truss
 * beams of `world.ts`. Beside a 1.8 m soldier the airframe reads as a vehicle, not a prop.
 */
export const HELI_HOVER_Y = 2.6;

/**
 * Motion constants — every pose term is built from these literals plus the shared tank constants
 * it delegates to. Amplitudes are tuned to the L3 strafe velocity scale: |d/dt bossStrafeX| peaks
 * at `strafeHalfWidth · 2π / strafePeriod ≈ 1.13 x-units/s` on the plan-D2 heli (0.65 / 3.6 s).
 */
export const HELI_MOTION = {
  /**
   * Hull roll (bank), rad per x-unit/s of strafe velocity. POSITIVE — the deliberate OPPOSITE
   * convention to `TANK_MOTION.rollPerVel`, which is applied NEGATIVELY in `tankRock`: a tracked
   * hull leans OUT of a slide (the tracks scrub), a rotorcraft banks INTO the direction it is
   * sliding toward (the disc tips that way to make it happen). Same magnitude class, other sign.
   */
  rollPerVel: 0.09,
  /** Nose dip with |strafe velocity| (rad per x-unit/s) — the disc's forward tilt as it slides. */
  pitchPerVel: 0.055,
  /** Hover bob: amplitude (m) and period (s) — a slow 0.15 m sin at ≈ 0.43 Hz. */
  hoverAmplitude: 0.15,
  hoverPeriodS: 2.3,
  /**
   * Main rotor spin rate (rad/s). At 60 fps that is 0.30 rad = 17.2° per frame against the 4-blade
   * 90° symmetry, i.e. 2.6× below the 45°/frame reversal threshold: the blades read as MOTION
   * rather than a reversing strobe, and the disc never disappears into a blur (D3).
   */
  rotorRadPerSec: 18,
  /** Tail rotor: a smaller disc geared faster. 2.2× ⇒ 0.66 rad/frame against 2-blade symmetry. */
  tailRotorRatio: 2.2,
  /** Spin-down: the corpse's rotor RATE reaches exactly 0 this many seconds after `wonAt` (D3). */
  spinDownS: 1.5,
  /** Rocket arc apex above the deck (m) — EXACT at u = 0.5 via the shared `arcPoint`. Flatter than
   *  the tank's 4.5 m shell: a rocket, not a lobbed howitzer round. */
  arcApex: 3.8,
  /** Death settle: the hull eases DOWN this far (m) over `settleS`, then rests on the wreck. */
  settleDrop: 0.8,
  settleS: 1.2,
  /** Death droop: the nose pitches OVER by this much (rad) over `droopS` (the tank's read). */
  droopRad: 0.34,
  droopS: 1.2,
  /** Death smoke plume: rise speed (m/s), per-puff stagger (s) and the final fade window (s). */
  plumeRise: 1.0,
  plumeStagger: 0.65,
  plumeFadeS: 1.2,
} as const;

/**
 * Authored rig numbers (metres, authored 1:1 — no scale factor; model faces −z toward the squad,
 * the muzzle side, exactly as the tank authors it). The pods sit at `HELI_ROCKET_DZ` along z and at
 * `podY` above the hull centre, so the rest-pose rendered pod IS the sim's `bossFire` origin —
 * heli.test.ts holds that pin the way tank.test.ts holds `TANK_MUZZLE_DZ`.
 */
export const HELI_PARTS = {
  /** Rocket-pod height above the hull centre (m) — world y = hover + 0.4 on stub-wing pylons. */
  podY: 0.4,
  /** Rocket-pod z in hull space: the SHARED sim constant (bossFire.z = boss.z + HELI_ROCKET_DZ). */
  podZ: HELI_ROCKET_DZ,
  /** Lateral pod centre (m): the pods flank the nose; the salvo origin is the centre-line between. */
  podX: 0.92,
  podRadius: 0.17,
  podLength: 1.5,
  /** Main rotor disc plane above the hull centre (m) — above the engine housing and mast. */
  rotorY: 1.42,
  rotorZ: 0.05,
  /** Main rotor blade span (m) and chord (m): Ø ≈ 4.6 m disc. */
  bladeLength: 2.2,
  bladeChord: 0.3,
  /** Tail rotor: hub offset from the hull centre, blade half-span (m). */
  tailX: 0.27,
  tailY: 0.95,
  tailZ: 2.62,
  tailBlade: 0.42,
  /** Skid rail centre height (m) — hull centre 2.6 m, so the skids clear the deck by ≈ 1.2 m. */
  skidY: -1.33,
  skidX: 0.72,
  skidLength: 3.2,
  /** Targeting reticle depth: the tank's constant, imported (one reticle shape on the bridge). */
  telegraphDepth: TANK_PARTS.telegraphDepth,
} as const;

// ---------------------------------------------------------------------------
// Pure pose helpers — every one is a closed-form function of the snapshot. No dt, no state.
// ---------------------------------------------------------------------------

/**
 * Hover height (m) at sim time t: `HELI_HOVER_Y` plus a small sin bob (0.15 m at 2.3 s). Pure in t,
 * and EXACTLY `HELI_HOVER_Y` at t = 0 — the rest-pose guard the other helpers carry (a paused/restarted
 * frame shows the authored hover, not a bob phase).
 */
export function heliHoverY(t: number): number {
  return HELI_HOVER_Y + HELI_MOTION.hoverAmplitude * Math.sin((2 * Math.PI * t) / HELI_MOTION.hoverPeriodS);
}

/**
 * Fuselage yaw (rotation.y) — the tank's aim/freeze/ease pattern VERBATIM, shared through
 * `tankAimYaw` (which is generic on the spec, so the heli's own `standoff` is the only input the
 * two bosses disagree on): track `squadX` while silent, FROZEN on the locked band centre through
 * telegraph and inFlight, then a smoothstep ease back to live tracking over `TANK_MOTION.aimEaseS`
 * (0.4 s) keyed on `slamAt`. Exported separately from the tank so the heli's test pins the freeze
 * and the ease on THIS actor's handle.
 */
export function heliAimYaw(spec: BossSpec, sim: SimState): number {
  return tankAimYaw(spec, sim);
}

/** Bank terms (radians): roll INTO lateral motion + a nose dip with |velocity|.
 * Pure f(spec, sim.t) — the strafe derivative is the shared `bossStrafeVel`, so the bank stops dead
 * exactly when the sim stops. (The tank's fire jolt has no heli analogue: a rocket salvo does not
 * kick a helicopter's attitude the way a main gun kicks a hull.) */
export function heliBank(spec: BossSpec, sim: SimState): { roll: number; pitch: number } {
  const v = bossStrafeVel(spec, sim.t);
  // POSITIVE rollPerVel: bank INTO the slide — see HELI_MOTION.rollPerVel for the sign argument.
  return {
    roll: HELI_MOTION.rollPerVel * v,
    pitch: HELI_MOTION.pitchPerVel * Math.abs(v),
  };
}

/** Wrap to [0, 2π) so a long fight keeps float precision without changing any blade attitude. */
function wrapAngle(a: number): number {
  const two = Math.PI * 2;
  return a - two * Math.floor(a / two);
}

/**
 * Main rotor blade angle (rad) at sim time t — `HELI_MOTION.rotorRadPerSec · t`, wrapped. The rotor's
 * REST pose IS spinning (D3): the guard the other helpers put on `bossFireAt < 0` is here the pinned
 * authored angle, `rotorSpin(0) === 0`, so a t = 0 / restart frame shows the authored blade cross.
 */
export function rotorSpin(t: number): number {
  return wrapAngle(HELI_MOTION.rotorRadPerSec * t);
}

/** Tail rotor angle (rad): the same closed form, geared `tailRotorRatio` faster (smaller disc). */
export function tailRotorSpin(t: number): number {
  return wrapAngle(HELI_MOTION.rotorRadPerSec * HELI_MOTION.tailRotorRatio * t);
}

/**
 * Corpse rotor RATE (rad/s) `deathAge` seconds after `wonAt`: linear decay from the live rate to
 * EXACTLY 0 at `HELI_MOTION.spinDownS`, clamped at 0 after (the disc is stopped, it does not
 * reverse). Pure f(deathAge), so the spin-down plays out identically on a replay and freezes on a
 * pause. The tank's droop analogue reads over `droopS`; the disc keeps turning a little longer
 * (1.5 s) than the nose finishes pitching over (1.2 s) — that ordering is the death read.
 */
export function rotorSpinDown(deathAge: number): number {
  if (!(deathAge > 0)) return HELI_MOTION.rotorRadPerSec;
  const u = 1 - deathAge / HELI_MOTION.spinDownS;
  return u > 0 ? HELI_MOTION.rotorRadPerSec * u : 0;
}

/**
 * Extra angle (rad) gained while spinning down — the exact integral of `rotorSpinDown`, so the corpse
 * disc continues from its live attitude with no snap: 0 at deathAge 0, saturating at
 * `½ · rotorRadPerSec · spinDownS` (≈ 13.5 rad ≈ 2.2 turns) once the disc has stopped.
 */
export function rotorCoast(deathAge: number): number {
  if (!(deathAge > 0)) return 0;
  const s = HELI_MOTION.spinDownS;
  const r = HELI_MOTION.rotorRadPerSec;
  const age = Math.min(deathAge, s);
  return r * (age - (age * age) / (2 * s));
}

/**
 * Death settle/droop terms for a corpse `deathAge` old: the hull eases DOWN `settleDrop` metres over
 * `settleS` and the nose pitches OVER `droopRad` radians over `droopS` (both clamped to their window,
 * so they arrive and then rest instead of running on). null/`deathAge < 0` ⇒ no terms.
 */
export function heliDeathPose(deathAge: number | null): { settle: number; droop: number } {
  if (deathAge === null || !(deathAge >= 0)) return { settle: 0, droop: 0 };
  return {
    settle: HELI_MOTION.settleDrop * clamp01(deathAge / HELI_MOTION.settleS),
    droop: HELI_MOTION.droopRad * clamp01(deathAge / HELI_MOTION.droopS),
  };
}

/**
 * The RENDERED rocket origin for a snapshot — the world position between the two pods on the hull
 * centre-line (the sim's `bossFire.x` IS the strafe x: no barrel to walk out along), at pod height,
 * composed with the SAME yaw/pitch/roll the scene graph applies (Babylon Euler =
 * RotationYawPitchRoll). At rest (squad on the strafe line, never fired) the z component is exactly
 * `spec.z + HELI_ROCKET_DZ`, the heli sibling of the tank's muzzle pin.
 */
export function heliMuzzle(spec: BossSpec, sim: SimState): TankPoint {
  const yaw = heliAimYaw(spec, sim);
  const bank = heliBank(spec, sim);
  const m = Matrix.RotationYawPitchRoll(yaw, bank.pitch, bank.roll);
  const p = Vector3.TransformCoordinates(new Vector3(0, HELI_PARTS.podY, HELI_PARTS.podZ), m);
  return {
    x: toWorldX(bossStrafeX(spec, sim.t)) + p.x,
    y: heliHoverY(sim.t) + p.y,
    z: spec.z + p.z,
  };
}

/** Is the render-only rocket alive for this snapshot? The tank's gate verbatim (`shellAlive`:
 * phase `boss`, a fire stamp, and an age inside `spec.shellFlight`) — the sim never detonates a
 * rocket whose boss died. */
export function heliRocketAlive(spec: BossSpec, sim: SimState): boolean {
  return shellAlive(spec, sim);
}

/** The rocket start: the rendered pod pose AS OF the fire moment (pure re-evaluation at
 * t = bossFireAt — the same trick as `shellStart`, so the tracer cannot drift off the pods). */
export function heliRocketStart(spec: BossSpec, sim: SimState): TankPoint {
  return heliMuzzle(spec, { ...sim, t: sim.bossFireAt });
}

/** The rocket end: the locked band centre at `tankBandZ(spec)` — the tank's `shellEnd` verbatim
 * (one band, one y, one z helper for both fire-cycle bosses). */
export function heliRocketEnd(spec: BossSpec, sim: SimState): TankPoint {
  return shellEnd(spec, sim);
}

/**
 * Deterministic rocket arc point at u ∈ [0, 1] — the SHARED `arcPoint` (plan-gate r1 copilot P2:
 * one ballistic maths for both tracers, not two copies) with the heli's pod/band endpoints and
 * `HELI_MOTION.arcApex` (apex EXACT at u = 0.5).
 */
export function heliRocketArc(spec: BossSpec, sim: SimState, u: number): TankPoint {
  return arcPoint(heliRocketStart(spec, sim), heliRocketEnd(spec, sim), u, HELI_MOTION.arcApex);
}

// ---------------------------------------------------------------------------
// Authored geometry (plan-05 primitives): merged boxes/cylinders, bossPlate/bossTrim/bossHide/
// bossGlow/bossBone atlas tiles, ONE shared boss material. Authored in metres, 1:1.
// ---------------------------------------------------------------------------

/**
 * Airframe group, authored in ROOT space; merged to `boss-torso` (probe watch + caster contract).
 * ~4.6 m nose-to-tail, 1.6 m wide cabin, skids ~1.2 m below the hull centre — beside a 1.8 m soldier
 * this is a vehicle, and it flies low enough to fill the standoff frame (D3 "genuine presence").
 */
function buildAirframe(scene: Scene, material: PBRMaterial): Mesh {
  return authoredGeometry('heliBody', scene, material, 'boss-torso').mesh;
}

/** Main rotor group, authored in ROTOR-NODE space (disc plane at local y 0); merged to `boss-rotor`.
 * Hub + 4 thin blades in a cross — the blades are thin enough that the disc reads as motion. */
function buildMainRotor(scene: Scene, material: PBRMaterial): Mesh {
  return authoredGeometry('heliRotor', scene, material, 'boss-rotor').mesh;
}

/** Tail rotor group, authored in TAIL-ROTOR-NODE space (disc in the local y–z plane, shaft along x);
 * merged to `boss-tail-rotor`. Smaller and geared faster than the main disc. */
function buildTailRotor(scene: Scene, material: PBRMaterial): Mesh {
  return authoredGeometry('heliTailRotor', scene, material, 'boss-tail-rotor').mesh;
}

// ---------------------------------------------------------------------------
// The actor.
// ---------------------------------------------------------------------------

export class HeliBoss {
  readonly root: TransformNode;
  readonly rotorNode: TransformNode;
  readonly tailRotorNode: TransformNode;
  readonly airframe: Mesh;
  readonly rotor: Mesh;
  readonly tailRotor: Mesh;
  readonly telegraph: Mesh;
  private readonly flash: Mesh;
  private readonly rocket: Mesh;
  private readonly puffs: Mesh[] = [];
  private readonly plume: Mesh[] = [];
  private readonly material: PBRMaterial;
  private readonly flashMat: StandardMaterial;
  private readonly puffMat: StandardMaterial;
  private readonly plumeMat: StandardMaterial;

  constructor(scene: Scene, materials: RenderMaterials, private readonly spec: LevelSpec['boss']) {
    this.material = materials.boss as PBRMaterial;
    this.root = new TransformNode('boss-root', scene);
    this.root.position.set(0, 0, spec.z);

    this.airframe = buildAirframe(scene, this.material);
    this.airframe.parent = this.root;
    this.rotorNode = new TransformNode('boss-rotor-node', scene);
    this.rotorNode.position.set(0, HELI_PARTS.rotorY, HELI_PARTS.rotorZ);
    this.rotorNode.parent = this.root;
    this.rotor = buildMainRotor(scene, this.material);
    this.rotor.parent = this.rotorNode;
    this.tailRotorNode = new TransformNode('boss-tail-rotor-node', scene);
    this.tailRotorNode.position.set(HELI_PARTS.tailX, HELI_PARTS.tailY, HELI_PARTS.tailZ);
    this.tailRotorNode.parent = this.root;
    this.tailRotor = buildTailRotor(scene, this.material);
    this.tailRotor.parent = this.tailRotorNode;

    // Targeting reticle at the locked band z: the tank's quad pattern VERBATIM (same width from the
    // sim zone, same depth, same red, same sim-time pulse — the shared TANK_PARTS constants), so one
    // dodge rule reads identically under either boss.
    this.telegraph = MeshBuilder.CreateGround(
      'heli-telegraph',
      { width: TANK_ZONE_HALF_X * 2 * BRIDGE_HALF_WIDTH, height: HELI_PARTS.telegraphDepth },
      scene,
    );
    this.telegraph.material = materials.glow(new Color3(1, 0.12, 0.05), TANK_PARTS.telegraphAlpha0 + TANK_PARTS.telegraphAlpha1);
    this.telegraph.position.y = 0.06;
    this.telegraph.isPickable = false;
    this.telegraph.setEnabled(false);

    this.flashMat = materials.glow(new Color3(2, 1.5, 0.7), 0.85);
    this.flash = MeshBuilder.CreatePlane('heli-muzzle-flash', { size: 1 }, scene);
    this.flash.material = this.flashMat;
    this.flash.isPickable = false;
    this.flash.setEnabled(false);

    const fx = (name: string, color: Color3): StandardMaterial => {
      const m = new StandardMaterial(name, scene);
      m.disableLighting = true;
      m.emissiveColor = color;
      m.alpha = 0;
      m.alphaMode = Constants.ALPHA_COMBINE; // smoke family: alpha blend, not additive
      m.backFaceCulling = false;
      m.disableDepthWrite = true;
      textureSmoke(m, scene);
      return m;
    };
    this.puffMat = fx('heli-rocket-puff-mat', new Color3(0.75, 0.7, 0.62));
    this.rocket = MeshBuilder.CreateCapsule(
      'heli-rocket',
      { height: 0.4, radius: 0.055, tessellation: 6, capSubdivisions: 2 },
      scene,
    );
    this.rocket.material = materials.glow(new Color3(2.6, 1.5, 0.6), 1);
    this.rocket.isPickable = false;
    this.rocket.setEnabled(false);
    for (let k = 0; k < TANK_MOTION.shellPuffs; k++) {
      const puff = MeshBuilder.CreatePlane(`heli-rocket-puff-${k}`, { size: 1 }, scene);
      puff.material = this.puffMat;
      puff.isPickable = false;
      puff.billboardMode = Mesh.BILLBOARDMODE_ALL;
      puff.setEnabled(false);
      this.puffs.push(puff);
    }

    // Death plume (alpha-blended dark smoke — additive would vanish against the dusk deck).
    this.plumeMat = fx('heli-plume-mat', new Color3(0.16, 0.15, 0.14));
    for (let k = 0; k < 3; k++) {
      const puff = MeshBuilder.CreatePlane(`heli-plume-${k}`, { size: 1 }, scene);
      puff.material = this.plumeMat;
      puff.isPickable = false;
      puff.billboardMode = Mesh.BILLBOARDMODE_ALL;
      puff.setEnabled(false);
      puff.parent = this.root;
      this.plume.push(puff);
    }
  }

  /**
   * Sync from the snapshot (pure — `dt` accepted for call-site parity, unused by contract).
   * Visible during 'boss' AND through the death-hold window after 'won' (spin-down + settle +
   * droop + plume, all f(deathAge)).
   */
  update(sim: SimState, _dt: number, presentationAge?: number): void {
    const deathAge = bossDeathAge(sim, presentationAge);
    const corpse = deathAge !== null && deathAge >= 0 && deathAge <= HELI_DEATH_HOLD;
    const active = sim.phase === 'boss' || (sim.phase === 'won' && corpse);
    this.root.setEnabled(active);
    this.telegraph.setEnabled(false);
    this.flash.setEnabled(false);
    this.rocket.setEnabled(false);
    for (const puff of this.puffs) puff.setEnabled(false);
    for (const puff of this.plume) puff.setEnabled(false);
    if (!active) {
      this.plumeMat.alpha = 0; // no stale smoke alpha left on the shared material
      return;
    }

    // Dying (the corpse window) is the ONLY state the death read needs, and it is derived from the
    // snapshot: combat sim.t stays frozen at wonAt while presentation age advances; every f(sim.t)
    // term (strafe, hover bob, rotor angle) already stops dead by itself.
    const dying = sim.phase === 'won' && deathAge !== null && deathAge >= 0;

    // Root: strafe x (f(sim.t), so it stops dead when the sim stops), hover y minus the death
    // settle, fixed z; attitude = bank + aim yaw + the death nose droop.
    const bank = heliBank(this.spec, sim);
    const death = heliDeathPose(dying ? deathAge : null);
    this.root.position.set(
      toWorldX(bossStrafeX(this.spec, sim.t)),
      heliHoverY(sim.t) - death.settle,
      this.spec.z,
    );
    this.root.rotation.set(bank.pitch + death.droop, heliAimYaw(this.spec, sim), bank.roll);

    // Rotors: the continuous spin, plus (dying only) the coast integral — continuous in BOTH angle
    // and rate at the death instant, then exactly stopped at `spinDownS` (no snap, no reverse).
    const coast = dying ? rotorCoast(deathAge!) : 0;
    this.rotorNode.rotation.y = rotorSpin(sim.t) + coast;
    this.tailRotorNode.rotation.x = tailRotorSpin(sim.t) + HELI_MOTION.tailRotorRatio * coast;

    // Slit emissives ramp at the lock — the shared boss-material read (`applyBossGlow` is generic on
    // slamPeriod/slamTelegraph, so the heli spec fits unchanged, exactly as the sim's fire cycle).
    applyBossGlow(this.material, sim, this.spec);

    this.updateTelegraph(sim);
    this.updateFlash(sim);
    this.updateRocket(sim);
    this.updatePlume(sim, dying ? deathAge : null);
  }

  /** Reticle: enabled through telegraph AND inFlight, zone-centre x, band z, zone width, sim-time
   * pulse — the tank's `updateTelegraph` pattern with the shared reticle constants. */
  private updateTelegraph(sim: SimState): void {
    const slam = sim.slam;
    const show = sim.phase === 'boss' && !!slam && (slam.active === 'telegraph' || slam.active === 'inFlight');
    this.telegraph.setEnabled(show);
    if (!show || !slam) return;
    const [lo, hi] = slam.zone;
    this.telegraph.position.x = ((lo + hi) / 2) * BRIDGE_HALF_WIDTH;
    this.telegraph.position.z = tankBandZ(this.spec);
    const authored = TANK_ZONE_HALF_X * 2 * BRIDGE_HALF_WIDTH;
    this.telegraph.scaling.x = Math.max(0.2, ((hi - lo) * BRIDGE_HALF_WIDTH) / authored);
    (this.telegraph.material as StandardMaterial).alpha =
      TANK_PARTS.telegraphAlpha0 + TANK_PARTS.telegraphAlpha1 * Math.sin(sim.t * TANK_PARTS.telegraphHz);
  }

  /** Additive pod flash: f(fire age ≤ TANK_MOTION.maxFlashS) at the rendered pod origin. */
  private updateFlash(sim: SimState): void {
    const age = sim.bossFireAt >= 0 ? sim.t - sim.bossFireAt : -1;
    const show = sim.phase === 'boss' && age >= 0 && age <= TANK_MOTION.maxFlashS;
    this.flash.setEnabled(show);
    if (!show || age < 0) return;
    const m = heliMuzzle(this.spec, sim);
    this.flash.position.set(m.x, m.y + 0.1, m.z);
    const u = age / TANK_MOTION.maxFlashS;
    const s = 1.5 + 1.0 * u;
    this.flash.scaling.set(s, s, 1);
    this.flashMat.alpha = 0.85 * (1 - u) + 0.1;
  }

  /** Emissive tracer on the shared arc + a fading puff chain behind it — all f(age), bounded by the
   * phase via `heliRocketAlive`. Sizes are the tank's shell read tuned down for a smaller rocket. */
  private updateRocket(sim: SimState): void {
    const alive = heliRocketAlive(this.spec, sim);
    this.rocket.setEnabled(alive);
    const flight = this.spec.shellFlight ?? 0;
    const age = sim.t - Math.max(sim.bossFireAt, 0);
    const u = alive && flight > 0 ? age / flight : 0;
    if (!alive) return;
    const head = heliRocketArc(this.spec, sim, u);
    const ahead = heliRocketArc(this.spec, sim, Math.min(1, u + 0.02));
    const dx = ahead.x - head.x;
    const dy = ahead.y - head.y;
    const dz = ahead.z - head.z;
    const len = Math.hypot(dx, dy, dz) || 1;
    // Orient the +y capsule onto the arc tangent (the actors.ts orientAlong maths, as in tank.ts).
    const ax = dz / len;
    const az = -dx / len;
    const n = Math.hypot(ax, az);
    this.rocket.rotationQuaternion =
      n < 1e-9
        ? Quaternion.Identity()
        : Quaternion.RotationAxis(new Vector3(ax / n, 0, az / n), Math.acos(dy / len));
    this.rocket.position.set(head.x, head.y, head.z);
    this.puffMat.alpha = 0.36 * (1 - u);
    for (let k = 0; k < this.puffs.length; k++) {
      const uk = Math.max(0, u - (k + 1) * 0.1);
      const p = heliRocketArc(this.spec, sim, uk);
      this.puffs[k].setEnabled(true);
      this.puffs[k].position.set(p.x, p.y, p.z);
      const s = 0.26 + 0.2 * k;
      this.puffs[k].scaling.set(s, s, 1);
    }
  }

  /** Death read: a pure f(deathAge) dark smoke plume over the corpse window, gone by the hold's end. */
  private updatePlume(sim: SimState, deathAge: number | null): void {
    const show = sim.phase === 'won' && deathAge !== null && deathAge >= 0 && deathAge <= HELI_DEATH_HOLD;
    if (!show || deathAge === null) {
      this.plumeMat.alpha = 0;
      return;
    }
    const fadeOut = 1 - clamp01((deathAge - (HELI_DEATH_HOLD - HELI_MOTION.plumeFadeS)) / HELI_MOTION.plumeFadeS);
    this.plumeMat.alpha = 0.5 * clamp01(deathAge / 0.4) * fadeOut;
    for (let k = 0; k < this.plume.length; k++) {
      const puff = this.plume[k]!;
      puff.setEnabled(true);
      // Root space: the plume rises from the settling engine deck (local y ≈ 0.9).
      puff.position.set(0.3 * Math.sin(deathAge * 1.7 + k * 2.1), 0.9 + k * HELI_MOTION.plumeStagger + deathAge * HELI_MOTION.plumeRise, 0.2);
      const s = 1.0 + k * 0.4 + deathAge * 0.85;
      puff.scaling.set(s, s, 1);
    }
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
